// src/pages/admin/AdminLoginPage.js
//
// Deliberately NOT linked from LoginPage.js or anywhere else in the public
// UI (Aug 16 2026 decision) — publicly advertising an admin sign-in tab
// on the same page patients/doctors use invites credential-stuffing
// attempts against the highest-privilege account type on this platform.
// Reached only via its direct URL: https://thyroconsult.agitomed.com/admin/login
// (shared out-of-band — bookmark/internal docs — not surfaced in-app).
// Same visual language as LoginPage.js (same card size/colours) so it
// doesn't look out of place if an admin does land here, but intentionally
// has no Patient/Doctor toggle, no language picker, no "create account"
// link — admins are provisioned directly, not self-registered.
import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Logo, Alert, Spinner } from '../../components/common/index';
import Turnstile from '../../components/Turnstile';

const AdminLoginPage = () => {
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // "I am human" check (A3, 13 Oct 2026): the server asks for it after
  // 2 wrong passwords, but this page had no box, so admin was then locked
  // out. The box is always shown; each pass works once, so it renews
  // after every failed sign-in.
  const [tsToken, setTsToken] = useState('');
  const [tsError, setTsError] = useState('');
  const tsRef = useRef(null);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      await login(identifier, password, 'admin', tsToken || undefined);
      navigate('/admin/dashboard', { replace: true });
    } catch (err) {
      const msg = err.response?.data?.error || 'Login failed. Please check your credentials.';
      setError(msg);
      if (tsRef.current) tsRef.current.reset();
    } finally { setLoading(false); }
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ width: '100%', maxWidth: 420 }}>
        {/* Logo & title */}
        <div style={{ textAlign: 'center', marginBottom: 36 }}>
          <Logo size="lg" />
          <h1 style={{ marginTop: 16, fontSize: '1.8rem', color: 'var(--text-primary)' }}>Admin sign in</h1>
          <p style={{ color: 'var(--text-tertiary)', fontSize: 14, marginTop: 6 }}>Authorised platform administrators only</p>
        </div>

        {/* Form */}
        <div className="card" style={{ padding: 28 }}>
          {error && <Alert type="error" onClose={() => setError('')}>{error}</Alert>}
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">Email address</label>
              <input className="form-input" type="text" value={identifier} onChange={e => setIdentifier(e.target.value)} placeholder="your@email.com" required autoFocus />
            </div>
            <div className="form-group" style={{ marginBottom: 24 }}>
              <label className="form-label">Password</label>
              <div style={{ position: 'relative' }}>
                <input className="form-input" type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} placeholder="Enter your password" required style={{ paddingRight: 44 }} />
                <button type="button" onClick={() => setShowPassword(!showPassword)} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-tertiary)', fontSize: 14 }}>
                  {showPassword ? '🙈' : '👁'}
                </button>
              </div>
            </div>
            <div style={{ marginBottom: 16 }}>
              <Turnstile ref={tsRef} onToken={(t) => { setTsToken(t); if (t) setTsError(''); }} onError={(c) => setTsError(String(c || 'error'))} action="admin_login" appearance="always" />
              {tsError && <div style={{ fontSize: 12, color: 'var(--red-600)', marginTop: 4 }}>The security check could not load (code {tsError}). Please reload the page and try again.</div>}
            </div>
            <button type="submit" className="btn btn-primary btn-lg" style={{ width: '100%', justifyContent: 'center' }} disabled={loading}>
              {loading ? <Spinner size={18} color="#fff" /> : 'Sign in'}
            </button>
          </form>
        </div>

        {/* Security notice */}
        <p style={{ textAlign: 'center', fontSize: 12, color: 'var(--text-tertiary)', marginTop: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}>
          🔒 End-to-end encrypted · Your data is protected
        </p>
      </div>
    </div>
  );
};

export default AdminLoginPage;
